import i18next, { changeLanguage, use, type FlatNamespace, type i18n as I18n, type InitOptions, type BackendModule, type ReadCallback, type Resource, type ResourceKey, type Services, type TFunction as I18nextTFunction, type TOptions, type ResourceLanguage, type KeyPrefix, type KeyPrefixSelector, type ParseKeys } from 'i18next';
import { type FallbackNs, type UseTranslationOptions, initReactI18next, useTranslation as useTranslationBase } from 'react-i18next';
import './i18next-resource-types';
export type { AppTFunction, LegacyStringTFunction, TFunction, ValidKeyPrefix } from './i18next-types';
export { Translation } from './i18next-translation';
export { Trans } from './i18next-trans';
import type { AppTFunction, LegacyStringTFunction, ValidKeyPrefix } from './i18next-types';

/**
 * useTranslation の options。
 * keyPrefix は string または selector 関数を受け付ける（string key 側は型チェックしない）。
 */
export type AppUseTranslationOptions<KPrefix = undefined> = Omit<UseTranslationOptions<undefined>, 'keyPrefix'> & {
  keyPrefix?: KPrefix;
};

/** react-i18next の UseTranslationResponse はタプル兼オブジェクトのため、利用する形だけ明示する */
export type AppUseTranslationResponse<Ns extends FlatNamespace | readonly FlatNamespace[] | undefined = undefined, KPrefix = undefined> = {
  t: AppTFunction<FallbackNs<Ns>, KPrefix>;
  i18n: I18n;
  ready: boolean;
};

/**
 * 移行期: t('dot.key') は型チェックなし、t(($) => $.a.b) は Selector API で型チェックする。
 * keyPrefix に selector を渡すと t の参照起点が prefix 配下にスコープされる。
 * @see https://www.locize.com/blog/i18next-typescript-selector-api
 */
export function useTranslation<const Ns extends FlatNamespace | readonly FlatNamespace[] | undefined = undefined, const KPrefix extends string | KeyPrefixSelector<FallbackNs<Ns>> | undefined = undefined>(ns?: Ns, options?: AppUseTranslationOptions<KPrefix>): AppUseTranslationResponse<Ns, ValidKeyPrefix<FallbackNs<Ns>, KPrefix>> {
  type ResolvedKPrefix = ValidKeyPrefix<FallbackNs<Ns>, KPrefix>;
  const {
    t,
    i18n,
    ready
  } = useTranslationBase(ns, options as UseTranslationOptions<KeyPrefix<FallbackNs<Ns>>>);
  return {
    t: t as AppTFunction<FallbackNs<Ns>, ResolvedKPrefix>,
    i18n,
    ready
  };
}

/**
 * i18next 直 import の t 代替（enableSelector 下でも string key を使う）
 */
const rawT = i18next.t.bind(i18next) as (key: string | string[] | ((src: never) => string), options?: string | TOptions, rest?: TOptions) => string;
export const legacyT = ((key, defaultValueOrOptions, options) => {
  if (typeof key === 'function') {
    return rawT(key, defaultValueOrOptions as TOptions | undefined);
  }
  if (typeof defaultValueOrOptions === 'string') {
    return rawT(key, {
      defaultValue: defaultValueOrOptions,
      ...options
    });
  }
  return rawT(key, defaultValueOrOptions, options);
}) as LegacyStringTFunction & {
  (selector: (src: never) => string, options?: TOptions): string;
};

// i18next関連の機能をエクスポート
export { changeLanguage, i18next, initReactI18next, type UseTranslationOptions, use, type InitOptions, type BackendModule, type ReadCallback, type Resource, type ResourceKey, type Services, type I18nextTFunction, type TOptions, type ResourceLanguage, type KeyPrefix, type KeyPrefixSelector, type ParseKeys };